import { useState, useCallback } from 'react';
import { useIsDesktop } from './useMediaQuery';

interface SidebarState {
  /** Whether the sidebar is currently expanded */
  isExpanded: boolean;
  /** Toggle sidebar between expanded and collapsed */
  toggleSidebar: () => void;
  /** Force expand the sidebar */
  expandSidebar: () => void;
  /** Force collapse the sidebar */
  collapseSidebar: () => void;
}

/**
 * Hook to manage sidebar expand/collapse state with responsive behavior
 *
 * On desktop (>= 768px): Sidebar is always expanded
 * On mobile (< 768px): Sidebar is collapsed by default, can be expanded via toggle
 */
export function useSidebarState(): SidebarState {
  const isDesktop = useIsDesktop();
  const [isManuallyExpanded, setIsManuallyExpanded] = useState(false);

  // On desktop: always expanded; on mobile: follow manual state
  const isExpanded = isDesktop || isManuallyExpanded;

  const toggleSidebar = useCallback(() => {
    if (!isDesktop) {
      setIsManuallyExpanded((prev) => !prev);
    }
  }, [isDesktop]);

  const expandSidebar = useCallback(() => setIsManuallyExpanded(true), []);
  const collapseSidebar = useCallback(() => setIsManuallyExpanded(false), []);

  return { isExpanded, toggleSidebar, expandSidebar, collapseSidebar };
}
